import React from 'react';
import { MailIcon, PenLineIcon } from 'lucide-react';
import type { ESignPackage, Participant, Timestamp } from '../../types/docmagic';
import { formatDate, formatTime } from '../../utils/datetime';
import { STAGES, isBorrowerRole, requiredSigners, stageIndex } from '../../utils/packageStatus';
interface ParticipantsTableProps {
  pkg: ESignPackage;
  onSendEmail: (p: Participant) => void;
  onSign: (p: Participant) => void;
}
const timestampColumns: {
  key: keyof Participant;
  label: string;
  tone?: string;
}[] = [{
  key: 'created',
  label: 'Created'
}, {
  key: 'started',
  label: 'Started'
}, {
  key: 'consented',
  label: 'Consented'
}, {
  key: 'viewed',
  label: 'Viewed'
}, {
  key: 'completed',
  label: 'Completed',
  tone: 'text-success font-semibold'
}, {
  key: 'declined',
  label: 'Declined',
  tone: 'text-error font-semibold'
}];
const roleStyles: Record<string, string> = {
  Borrower: 'bg-[#eef4ff] text-[#0b5cab]',
  'Co-Borrower': 'bg-[#eef4ff] text-[#0b5cab]',
  Originator: 'bg-canvas text-muted'
};
export function ParticipantsTable({
  pkg,
  onSendEmail,
  onSign
}: ParticipantsTableProps) {
  const required = requiredSigners(pkg);
  const done = required.filter(p => p.completed).length;
  const pct = required.length ? Math.round(done / required.length * 100) : 0;
  return <section aria-labelledby="participants-heading" data-id="element-6" data-mp="26e0gry9l88eg">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-7" data-mp="26c7cgpmg609m">
        <div data-id="element-8" data-mp="1ip7hpsxo5kfm">
          <h3 id="participants-heading" className="text-sm font-bold text-ink" data-id="element-9" data-mp="1ioz41ytq2b3d">
            Participants ({pkg.participants.length})
          </h3>
          <p className="mt-0.5 text-xs text-muted" data-id="element-10" data-mp="1sf0vn0laabn1">
            {pkg.requireOriginatorCompletion ? 'Borrowers and originator must complete before the signed PDF is retrieved.' : 'Signed PDF is retrieved when all borrowers complete. Originator status is not required.'}
          </p>
        </div>
        <div className="w-full sm:w-64" data-id="element-11" data-mp="uybu7808xbe6">
          <div className="flex justify-between text-xs" data-id="element-12" data-mp="uxah6xzo6ett">
            <span className="text-muted" data-id="element-13" data-mp="1kmh8y3ak8nqw">Required signers</span>
            <span className="font-semibold text-ink" data-id="element-14" data-mp="1oolyaptz6y7q">
              {done} of {required.length} completed
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-line" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Required signer completion" data-id="element-15" data-mp="1tgvpsaaqo6c4">
            <div className="h-full rounded-full bg-success transition-all" style={{
            width: `${pct}%`
          }} data-id="element-16" data-mp="15p85ib7zftit" />
          </div>
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded border border-line" data-id="element-17" data-mp="1ar5vdoaw1mkq">
        <table className="w-full min-w-[1080px] border-collapse text-sm" data-id="element-18" data-mp="1foz1khc6ygl0">
          <thead data-id="element-19" data-mp="2m5170n2l3d77">
            <tr className="bg-canvas text-left text-xs font-semibold text-muted" data-id="element-20" data-mp="2m3gf757nm8d1">
              <th scope="col" className="w-10 px-3 py-2" data-id="element-21" data-mp="2r2jh4gsc7ygs">#</th>
              <th scope="col" className="px-3 py-2" data-id="element-22" data-mp="ffzjwvoqia7r">Name / Email</th>
              <th scope="col" className="px-3 py-2" data-id="element-23" data-mp="ff7n5hjei6fw">Role</th>
              {timestampColumns.map(c => <th key={c.key} scope="col" className="px-3 py-2" data-id="element-24" data-mp="aizemmzplbhp">{c.label}</th>)}
              <th scope="col" className="px-3 py-2 text-right" data-id="element-25" data-mp="5ocxs4auyqq8">Links</th>
            </tr>
          </thead>
          <tbody data-id="element-26" data-mp="p0e1sfa9zrv">
            {pkg.participants.map(p => {
            const stage = stageIndex(p);
            const finished = Boolean(p.completed || p.declined);
            const isRequired = isBorrowerRole(p) || pkg.requireOriginatorCompletion && p.role === 'Originator';
            return <tr key={p.referenceId} className="border-t border-line align-top hover:bg-[#fafafa]" data-id="element-27" data-mp="3dxmos17gj16r">
                  <td className="px-3 py-3 text-muted" data-id="element-28" data-mp="1h5cpaxpdt1j7">{p.sequence}</td>
                  <td className="px-3 py-3" data-id="element-29" data-mp="1h4jqqk0ktic5">
                    <p className="font-semibold text-ink" data-id="element-30" data-mp="1c5qgr1ki09gq">{p.name}</p>
                    <p className="text-xs text-muted" data-id="element-31" data-mp="1c4fcz1r1z1pm">{p.email}</p>
                    <div className="mt-1.5 flex gap-1" aria-label={`Progress: ${stage >= 0 ? STAGES[stage] : 'not started'}`} data-id="element-32" data-mp="1k7h7p3esv1oj">
                      {STAGES.map((s, i) => <span key={s} className={`h-1 w-5 rounded-full ${p.declined ? 'bg-error' : i <= stage ? stage === 4 ? 'bg-success' : 'bg-brand' : 'bg-line'}`} data-id="element-33" data-mp="1a7qdz3y2cqpi" data-mpi={i} />)}
                    </div>
                  </td>
                  <td className="px-3 py-3" data-id="element-34" data-mp="riipk6aofzj8">
                    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${roleStyles[p.role] ?? 'bg-canvas text-muted'}`} data-id="element-35" data-mp="rjtu48by5bbe">
                      {p.role}
                    </span>
                    {isRequired && <p className="mt-1 text-[11px] text-muted" data-id="element-36" data-mp="ijow2osn7p9w">Required signer</p>}
                  </td>
                  {timestampColumns.map(c => <td key={c.key} className="whitespace-nowrap px-3 py-3" data-id="element-37" data-mp="3u3s0fs1fp63">
                      <TimestampCell value={p[c.key] as Timestamp} tone={c.tone} data-id="element-38" data-mp="3v1gacy4nq8nl" />
                    </td>)}
                  <td className="px-3 py-3 text-right" data-id="element-39" data-mp="cwmngethuzib">
                    <div className="flex flex-col items-end gap-1" data-id="element-40" data-mp="7wgv0wfeh8z6">
                      <button type="button" onClick={() => onSendEmail(p)} className="inline-flex items-center gap-1 text-sm text-brand hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-41" data-mp="7v5rmxpva38l">
                        <MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-42" data-mp="vnw02wyka4e4" /> Send Email
                      </button>
                      <button type="button" onClick={() => onSign(p)} disabled={finished} className="inline-flex items-center gap-1 text-sm text-brand hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:text-line-strong disabled:no-underline" data-id="element-43" data-mp="15jzdiyyv5pgd">
                        <PenLineIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-44" data-mp="77d9r3vvhlzo" /> Sign Documents
                      </button>
                      <span className="font-mono text-[11px] text-muted" data-id="element-45" data-mp="c0fucmio9l3q">{p.referenceId}</span>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </section>;
}
function TimestampCell({
  value,
  tone
}: {
  value: Timestamp;
  tone?: string;
}) {
  if (!value) return <span className="sr-only" data-id="element-46" data-mp="uyiqdl9n0ib1">Not recorded</span>;
  return <time dateTime={value} className={`block leading-tight ${tone ?? 'text-ink'}`} data-id="element-47" data-mp="uvwh9h73luqp">
      <span className="block" data-id="element-48" data-mp="2rs3pqdi1f5kf">{formatDate(value)}</span>
      <span className="block text-xs font-normal text-muted" data-id="element-49" data-mp="2wou50ym0px4s">{formatTime(value)}</span>
    </time>;
}